// CareLogix™ ORACLE — Privacy & Risk Settings drawer (Phase 32.1)
// Lightweight modal/drawer exposed from the Acknowledgement Gate.
// Shows toggles for ORACLE-side privacy preferences and a Reset Acknowledgement action.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import { setAcknowledged } from "@/components/gate/OracleAcknowledgement";
import { setPrivacyAcknowledged } from "@/components/gate/CareLogixPrivacyGate";
import { setMembershipIntent } from "@/components/gate/MembershipSelectionGate";

const PREF_STORAGE_KEY = "oracle_privacy_prefs_v1";

const DEFAULT_PREFS = {
  voice_intelligence: true,
  image_intelligence: true,
  personalized_recommendations: true,
  personalized_chyron: true,
  notification_email: true,
  notification_sms: false,
  research_only_label_visible: true,
};

function loadPrefs() {
  if (typeof window === "undefined") return { ...DEFAULT_PREFS };
  try {
    const raw = window.localStorage.getItem(PREF_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch (_) { return { ...DEFAULT_PREFS }; }
}

function savePrefs(next) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(PREF_STORAGE_KEY, JSON.stringify(next)); } catch (_) { /* ignore */ }
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={!!checked}
      style={{ ...S.toggle, ...(checked ? S.toggleOn : null) }}
      data-testid={testid}
    >
      <span style={{ ...S.knob, ...(checked ? S.knobOn : null) }} aria-hidden="true" />
    </button>
  );
}

function Row({ label, hint, checked, onChange, testid }) {
  return (
    <div style={S.row} data-testid={`${testid}-row`}>
      <div style={S.rowText}>
        <div style={S.rowLabel}>{label}</div>
        {hint && <div style={S.rowHint}>{hint}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} testid={testid} />
    </div>
  );
}

export default function OraclePrivacyRiskSettings({ open, onClose, testid = "oracle-privacy-risk" }) {
  const [prefs, setPrefs] = useState(() => loadPrefs());

  useEffect(() => {
    if (open) setPrefs(loadPrefs());
  }, [open]);

  const setPref = (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    savePrefs(next);
  };

  const resetAck = () => {
    setAcknowledged(false);
    onClose && onClose();
  };

  const resetAllGates = () => {
    setPrivacyAcknowledged(false);
    setAcknowledged(false);
    setMembershipIntent(null);
    onClose && onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          style={S.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          data-testid={`${testid}-backdrop`}
        >
          <motion.aside
            style={S.panel}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${testid}-title`}
            data-testid={testid}
          >
            <header style={S.header}>
              <h2 style={S.title} id={`${testid}-title`} data-testid={`${testid}-title`}>ORACLE Privacy & Risk Settings</h2>
              <button type="button" onClick={onClose} style={S.closeBtn} data-testid={`${testid}-close`} aria-label="Close settings">×</button>
            </header>
            <p style={S.sub}>
              Tune what ORACLE may use to personalize and notify. Settings are saved on this device. Compliance Lock™ always applies.
            </p>

            <section style={S.section}>
              <h3 style={S.sectionTitle}>Intelligence Modalities</h3>
              <Row label="Voice intelligence" hint="LOGIX-2™ voice briefings and microphone input."
                   checked={prefs.voice_intelligence} onChange={(v) => setPref("voice_intelligence", v)}
                   testid={`${testid}-voice`} />
              <Row label="Image intelligence" hint="Chart/screenshot analysis."
                   checked={prefs.image_intelligence} onChange={(v) => setPref("image_intelligence", v)}
                   testid={`${testid}-image`} />
            </section>

            <section style={S.section}>
              <h3 style={S.sectionTitle}>Personalization</h3>
              <Row label="Personalized recommendations" hint="Use my interests, regions, risk style, and watchlist."
                   checked={prefs.personalized_recommendations} onChange={(v) => setPref("personalized_recommendations", v)}
                   testid={`${testid}-personalized-recs`} />
              <Row label="Personalized chyron" hint="Adapt the lower-third ticker to my profile."
                   checked={prefs.personalized_chyron} onChange={(v) => setPref("personalized_chyron", v)}
                   testid={`${testid}-personalized-chyron`} />
            </section>

            <section style={S.section}>
              <h3 style={S.sectionTitle}>Notifications</h3>
              <Row label="Email briefings" hint="9 AM CEO email briefing (admin-only when configured)."
                   checked={prefs.notification_email} onChange={(v) => setPref("notification_email", v)}
                   testid={`${testid}-notif-email`} />
              <Row label="SMS alerts" hint="8 AM SMS briefing (admin-only when configured)."
                   checked={prefs.notification_sms} onChange={(v) => setPref("notification_sms", v)}
                   testid={`${testid}-notif-sms`} />
            </section>

            <section style={S.section}>
              <h3 style={S.sectionTitle}>Display</h3>
              <Row label="Show research-only labels" hint="Always visible compliance text on every output (recommended)."
                   checked={prefs.research_only_label_visible} onChange={(v) => setPref("research_only_label_visible", v)}
                   testid={`${testid}-research-only-label`} />
            </section>

            <section style={S.dangerZone}>
              <h3 style={S.sectionTitle}>Reset</h3>
              <p style={S.dangerHint}>Show the ORACLE acknowledgement again on next launch.</p>
              <button
                type="button"
                onClick={resetAck}
                style={S.dangerBtn}
                data-testid={`${testid}-reset-ack`}
              >
                Reset ORACLE acknowledgement
              </button>
              <p style={{ ...S.dangerHint, marginTop: 8 }}>
                Or replay the full 3-step constitutional gating (Privacy → ORACLE Risk → Membership) on next launch.
              </p>
              <button
                type="button"
                onClick={resetAllGates}
                style={{ ...S.dangerBtn, marginTop: 2 }}
                data-testid={`${testid}-reset-all-gates`}
              >
                Reset all 3 gates
              </button>
            </section>

            <footer style={S.footer}>
              <button type="button" onClick={onClose} style={S.doneBtn} data-testid={`${testid}-done`}>
                Done
              </button>
            </footer>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    padding: 12,
  },
  panel: {
    width: "100%",
    maxWidth: 620,
    maxHeight: "min(92vh, 760px)",
    overflowY: "auto",
    background: "linear-gradient(180deg, rgba(11,15,20,0.98), rgba(6,18,12,0.98))",
    border: `1px solid ${PALETTE.goldGlow}`,
    borderRadius: 18,
    padding: 18,
    color: PALETTE.slate3,
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  title:  { margin: 0, color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.04em" },
  closeBtn: { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.slate3, width: 34, height: 34, borderRadius: 999, fontSize: 22, cursor: "pointer", lineHeight: 1 },
  sub:    { margin: 0, fontSize: 12.5, color: PALETTE.slate4, lineHeight: 1.5 },
  section:{ display: "flex", flexDirection: "column", gap: 6, background: "rgba(4,6,8,0.6)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12 },
  sectionTitle: { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.16em" },
  row:    { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "6px 0" },
  rowText:{ display: "flex", flexDirection: "column", gap: 2 },
  rowLabel:{ color: PALETTE.slate3, fontSize: 13.5, fontWeight: 600 },
  rowHint: { color: PALETTE.slate4, fontSize: 11.5, lineHeight: 1.35 },
  toggle: { appearance: "none", width: 42, height: 24, borderRadius: 999, border: `1px solid ${PALETTE.goldGlow}`, background: "#0b0f14", position: "relative", cursor: "pointer", padding: 0 },
  toggleOn: { background: "linear-gradient(90deg, #c5a059 0%, #fde68a 100%)", borderColor: "#c5a059" },
  knob:   { position: "absolute", top: 2, left: 2, width: 18, height: 18, borderRadius: "50%", background: "#1f2933", boxShadow: "0 1px 4px rgba(0,0,0,0.4)", transition: "transform .2s ease" },
  knobOn: { transform: "translateX(18px)", background: "#0b0f14" },
  dangerZone: { display: "flex", flexDirection: "column", gap: 6, background: "rgba(239,68,68,0.05)", border: `1px solid ${PALETTE.red}`, borderRadius: 12, padding: 12 },
  dangerHint: { margin: 0, color: PALETTE.slate4, fontSize: 12, lineHeight: 1.4 },
  dangerBtn:  { appearance: "none", background: "transparent", color: PALETTE.red, border: `1px solid ${PALETTE.red}`, padding: "8px 14px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontWeight: 700, letterSpacing: "0.08em", cursor: "pointer", alignSelf: "flex-start" },
  footer: { display: "flex", justifyContent: "flex-end" },
  doneBtn:{ appearance: "none", background: PALETTE.gold, color: "#0b0f14", border: "1px solid #c5a059", padding: "8px 18px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontWeight: 800, letterSpacing: "0.10em", cursor: "pointer" },
};
